import React, { useState, useEffect, useMemo } from "react";
import { Badge, Button, Col, Form, Image, InputGroup, Modal, Row, Table, Nav, Alert, Spinner, Card, Tabs, Tab } from "react-bootstrap";
import { oneKlicks } from '../../SDK/oneclick';
import ImageShow from '../../Uitiles/ImageShow';
import { toast } from 'react-toastify';

const HolidaysLeavesManagement = () => {
    // State management
    const [activeTab, setActiveTab] = useState('holidays');
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    
    // Holidays states
    const [holidaysList, setHolidaysList] = useState([]);
    const [showHolidayModal, setShowHolidayModal] = useState(false);
    const [showMonthlyHolidaysModal, setShowMonthlyHolidaysModal] = useState(false);
    const [monthlyHolidaysMonth, setMonthlyHolidaysMonth] = useState('');
    const [monthlyWeekendDays, setMonthlyWeekendDays] = useState([]);
    const [selectedMonthlyWeekendDates, setSelectedMonthlyWeekendDates] = useState([]);
    const [savingMonthlyHolidays, setSavingMonthlyHolidays] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState(null);
    const [holidayForm, setHolidayForm] = useState({
        name: '',
        date: '',
        type: 'national',
        description: '',
        is_recurring: false
    });
    
    // Leaves states
    const [leavesList, setLeavesList] = useState([]);
    const [employeesList, setEmployeesList] = useState([]);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [editingLeave, setEditingLeave] = useState(null);
    const [leaveForm, setLeaveForm] = useState({
        employee_id: '',
        leave_type: 'sick',
        start_date: '',
        end_date: '',
        days: 0,
        reason: '',
        status: 'pending'
    });
    
    // Leave balances states
    const [leaveBalances, setLeaveBalances] = useState([]);

    const existingHolidayDates = useMemo(() => {
        return new Set(holidaysList.map(holiday => (holiday.date || '').slice(0, 10)));
    }, [holidaysList]);

    useEffect(() => {
        fetchHolidaysList();
        fetchLeavesList();
        fetchEmployeesList();
        fetchLeaveBalances();
    }, []);

    // Holidays Functions
    const fetchHolidaysList = async () => {
        setLoading(true);
        try {
            const tableSchema = '';
            const tableName = 'holidays';
            const conditions = {};
            const orderBy = 'date ASC';
            const search = {};
            const pagination = {};

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination);
            
            if (response.success) {
                setHolidaysList(response.data);
            } else {
                toast.error('Failed to fetch holidays list');
            }
        } catch (error) {
            console.error('Error fetching holidays:', error);
            toast.error('Error fetching holidays list');
        } finally {
            setLoading(false);
        }
    };

    const handleAddHoliday = () => {
        setEditingHoliday(null);
        setHolidayForm({
            name: '',
            date: '',
            type: 'national',
            description: '',
            is_recurring: false
        });
        setShowHolidayModal(true);
    };

    const handleAddMonthlyHolidays = () => {
        setShowMonthlyHolidaysModal(true);
        setMonthlyHolidaysMonth('');
        setMonthlyWeekendDays([]);
        setSelectedMonthlyWeekendDates([]);
    };

    const handleMonthlyHolidaysModalClose = () => {
        setShowMonthlyHolidaysModal(false);
        setMonthlyHolidaysMonth('');
        setMonthlyWeekendDays([]);
        setSelectedMonthlyWeekendDates([]);
    };

    const generateWeekendDays = (year, month) => {
        const monthNumber = Number(month);
        if (!year || Number.isNaN(monthNumber)) {
            return [];
        }

        const daysInMonth = new Date(Number(year), monthNumber, 0).getDate();
        const weekendDays = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dateObj = new Date(`${dateKey}T00:00:00`);
            const dayOfWeek = dateObj.getDay();

            if (dayOfWeek === 0 || dayOfWeek === 6) {
                const dayName = dateObj.toLocaleDateString('en-IN', { weekday: 'long' });
                weekendDays.push({
                    date: dateKey,
                    dayName,
                    existing: existingHolidayDates.has(dateKey)
                });
            }
        }

        return weekendDays;
    };

    const handleMonthlyMonthChange = (value) => {
        setMonthlyHolidaysMonth(value);

        if (!value) {
            setMonthlyWeekendDays([]);
            setSelectedMonthlyWeekendDates([]);
            return;
        }

        const [year, month] = value.split('-');

        if (!year || !month) {
            setMonthlyWeekendDays([]);
            setSelectedMonthlyWeekendDates([]);
            return;
        }

        const weekends = generateWeekendDays(year, month);
        setMonthlyWeekendDays(weekends);
        setSelectedMonthlyWeekendDates(weekends.filter(day => !day.existing).map(day => day.date));
    };

    const handleToggleMonthlyWeekendDate = (date) => {
        setSelectedMonthlyWeekendDates(prev => {
            if (prev.includes(date)) {
                return prev.filter(item => item !== date);
            }
            return [...prev, date];
        });
    };

    const handleEditHoliday = (holiday) => {
        setEditingHoliday(holiday);
        setHolidayForm({
            name: holiday.name,
            date: holiday.date,
            type: holiday.type,
            description: holiday.description || '',
            is_recurring: holiday.is_recurring || false
        });
        setShowHolidayModal(true);
    };

    const handleSaveHoliday = async () => {
        try {
            const tableSchema = '';
            const tableName = 'holidays';
            
            const holidayData = {
                name: holidayForm.name,
                date: holidayForm.date,
                type: holidayForm.type,
                description: holidayForm.description,
                is_recurring: holidayForm.is_recurring
            };

            let response;
            if (editingHoliday) {
                const conditions = { id: editingHoliday.id };
                response = await oneKlicks.update(tableSchema, tableName, holidayData, conditions);
            } else {
                const conditions = {};
                response = await oneKlicks.create(tableSchema, tableName, holidayData, conditions);
            }

            if (response.success) {
                toast.success(editingHoliday ? 'Holiday updated successfully' : 'Holiday added successfully');
                setShowHolidayModal(false);
                fetchHolidaysList();
            } else {
                toast.error('Failed to save holiday');
            }
        } catch (error) {
            console.error('Error saving holiday:', error);
            toast.error('Error saving holiday');
        }
    };

    const handleSaveMonthlyHolidays = async () => {
        if (!monthlyHolidaysMonth) {
            toast.error('Please select a month');
            return;
        }

        const datesToCreate = selectedMonthlyWeekendDates.filter(date => !existingHolidayDates.has(date));

        if (datesToCreate.length === 0) {
            toast.error('Select at least one weekend day to add');
            return;
        }

        setSavingMonthlyHolidays(true);

        try {
            const tableSchema = '';
            const tableName = 'holidays';

            for (const date of datesToCreate) {
                const weekendMeta = monthlyWeekendDays.find(day => day.date === date);
                const dayName = weekendMeta?.dayName || 'Weekend';

                const holidayData = {
                    name: `${dayName} Off`,
                    date,
                    type: 'company',
                    description: 'Monthly weekend holiday',
                    is_recurring: false
                };

                await oneKlicks.create(tableSchema, tableName, holidayData, {});
            }

            toast.success('Monthly weekend holidays added successfully');
            handleMonthlyHolidaysModalClose();
            fetchHolidaysList();
        } catch (error) {
            console.error('Error adding monthly holidays:', error);
            toast.error('Failed to add monthly holidays');
        } finally {
            setSavingMonthlyHolidays(false);
        }
    };

    const handleDeleteHoliday = async (holidayId) => {
        if (window.confirm('Are you sure you want to delete this holiday?')) {
            try {
                const tableSchema = '';
                const tableName = 'holidays';
                const conditions = { id: holidayId };
                
                const response = await oneKlicks.delete(tableSchema, tableName, conditions);
                
                if (response.success) {
                    toast.success('Holiday deleted successfully');
                    fetchHolidaysList();
                } else {
                    toast.error('Failed to delete holiday');
                }
            } catch (error) {
                console.error('Error deleting holiday:', error);
                toast.error('Error deleting holiday');
            }
        }
    };

    // Leaves Functions
    const fetchLeavesList = async () => {
        try {
            const tableSchema = '';
            const tableName = 'leave_requests';
            const conditions = {};
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'employees',
                    target_column: 'id',
                    target_value: 'employee_id',
                    target_label: 'employee',
                    margedata: [
                        {
                            target_table: 'users',
                            target_column: 'id',
                            target_value: 'staff_id',
                            target_label: 'staff'
                        }
                    ]
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setLeavesList(response.data);
            } else {
                toast.error('Failed to fetch leaves list');
            }
        } catch (error) {
            console.error('Error fetching leaves:', error);
            toast.error('Error fetching leaves list');
        }
    };

    const fetchEmployeesList = async () => {
        try {
            const tableSchema = '';
            const tableName = 'employees';
            const conditions = { status: 'active' };
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'users',
                    target_column: 'id',
                    target_value: 'staff_id',
                    target_label: 'staff'
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setEmployeesList(response.data);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const fetchLeaveBalances = async () => {
        try {
            const tableSchema = '';
            const tableName = 'leave_balances';
            const conditions = {};
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'employees',
                    target_column: 'id',
                    target_value: 'employee_id',
                    target_label: 'employee',
                    margedata: [
                        {
                            target_table: 'users',
                            target_column: 'id',
                            target_value: 'staff_id',
                            target_label: 'staff'
                        }
                    ]
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setLeaveBalances(response.data);
            }
        } catch (error) {
            console.error('Error fetching leave balances:', error);
        }
    };

    const handleAddLeave = () => {
        setEditingLeave(null);
        setLeaveForm({
            employee_id: '',
            leave_type: 'sick',
            start_date: '',
            end_date: '',
            days: 0,
            reason: '',
            status: 'pending'
        });
        setShowLeaveModal(true);
    };

    const handleEditLeave = (leave) => {
        setEditingLeave(leave);
        setLeaveForm({
            employee_id: leave.employee_id,
            leave_type: leave.leave_type,
            start_date: leave.start_date,
            end_date: leave.end_date,
            days: leave.days,
            reason: leave.reason || '',
            status: leave.status
        });
        setShowLeaveModal(true);
    };

    const handleSaveLeave = async () => {
        try {
            const tableSchema = '';
            const tableName = 'leave_requests';
            
            const leaveData = {
                employee_id: parseInt(leaveForm.employee_id),
                leave_type: leaveForm.leave_type,
                start_date: leaveForm.start_date,
                end_date: leaveForm.end_date,
                days: parseInt(leaveForm.days),
                reason: leaveForm.reason,
                status: leaveForm.status,
                created_at: new Date().toISOString()
            };

            let response;
            if (editingLeave) {
                const conditions = { id: editingLeave.id };
                response = await oneKlicks.update(tableSchema, tableName, leaveData, conditions);
            } else {
                const conditions = {};
                response = await oneKlicks.create(tableSchema, tableName, leaveData, conditions);
            }

            if (response.success) {
                toast.success(editingLeave ? 'Leave request updated successfully' : 'Leave request added successfully');
                setShowLeaveModal(false);
                fetchLeavesList();
                fetchLeaveBalances();
            } else {
                toast.error('Failed to save leave request');
            }
        } catch (error) {
            console.error('Error saving leave request:', error);
            toast.error('Error saving leave request');
        }
    };

    const handleApproveLeave = async (leaveId) => {
        try {
            const tableSchema = '';
            const tableName = 'leave_requests';
            const conditions = { id: leaveId };
            const updateData = {
                status: 'approved',
                approved_at: new Date().toISOString()
            };

            const response = await oneKlicks.update(tableSchema, tableName, updateData, conditions);
            
            if (response.success) {
                toast.success('Leave request approved successfully');
                fetchLeavesList();
                fetchLeaveBalances();
            } else {
                toast.error('Failed to approve leave request');
            }
        } catch (error) {
            console.error('Error approving leave:', error);
            toast.error('Error approving leave request');
        }
    };

    const handleRejectLeave = async (leaveId) => {
        try {
            const tableSchema = '';
            const tableName = 'leave_requests';
            const conditions = { id: leaveId };
            const updateData = {
                status: 'rejected',
                rejected_at: new Date().toISOString()
            };

            const response = await oneKlicks.update(tableSchema, tableName, updateData, conditions);
            
            if (response.success) {
                toast.success('Leave request rejected successfully');
                fetchLeavesList();
            } else {
                toast.error('Failed to reject leave request');
            }
        } catch (error) {
            console.error('Error rejecting leave:', error);
            toast.error('Error rejecting leave request');
        }
    };

    // Filter functions
    const filteredHolidays = useMemo(() => {
        if (!search.trim()) return holidaysList;
        
        return holidaysList.filter(holiday => {
            const name = holiday.name?.toLowerCase() || '';
            const type = holiday.type?.toLowerCase() || '';
            const description = holiday.description?.toLowerCase() || '';
            
            const searchTerm = search.toLowerCase();
            
            return name.includes(searchTerm) || 
                   type.includes(searchTerm) || 
                   description.includes(searchTerm);
        });
    }, [holidaysList, search]);

    const filteredLeaves = useMemo(() => {
        if (!search.trim()) return leavesList;
        
        return leavesList.filter(leave => {
            const employeeName = `${leave.employee?.[0]?.staff?.[0]?.first_name || ''} ${leave.employee?.[0]?.staff?.[0]?.last_name || ''}`.toLowerCase();
            const leaveType = leave.leave_type?.toLowerCase() || '';
            const reason = leave.reason?.toLowerCase() || '';
            const status = leave.status?.toLowerCase() || '';
            
            const searchTerm = search.toLowerCase();
            
            return employeeName.includes(searchTerm) || 
                   leaveType.includes(searchTerm) || 
                   reason.includes(searchTerm) ||
                   status.includes(searchTerm);
        });
    }, [leavesList, search]);

    // Utility functions
    const getHolidayTypeBadge = (type) => {
        const typeConfig = {
            national: { variant: 'primary', text: 'National' },
            religious: { variant: 'info', text: 'Religious' },
            regional: { variant: 'success', text: 'Regional' },
            company: { variant: 'warning', text: 'Company' },
            other: { variant: 'secondary', text: 'Other' }
        };
        
        const config = typeConfig[type] || typeConfig.other;
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getLeaveTypeBadge = (type) => {
        const typeConfig = {
            sick: { variant: 'danger', text: 'Sick Leave' },
            casual: { variant: 'info', text: 'Casual Leave' },
            annual: { variant: 'success', text: 'Annual Leave' },
            maternity: { variant: 'warning', text: 'Maternity Leave' },
            paternity: { variant: 'primary', text: 'Paternity Leave' },
            emergency: { variant: 'dark', text: 'Emergency Leave' },
            unpaid: { variant: 'secondary', text: 'Unpaid Leave' }
        };
        
        const config = typeConfig[type] || typeConfig.casual;
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getLeaveStatusBadge = (status) => {
        const statusConfig = {
            pending: { variant: 'warning', text: 'Pending' },
            approved: { variant: 'success', text: 'Approved' },
            rejected: { variant: 'danger', text: 'Rejected' },
            cancelled: { variant: 'secondary', text: 'Cancelled' }
        };
        
        const config = statusConfig[status] || statusConfig.pending;
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    const calculateDays = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    return (
        <div className="dc-haslayout dc-dbsectionspace">
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">Holidays & Leaves Management</h5>
                                <Badge bg="info">HRM System</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="p-0" style={{ maxWidth: 400, flex: "1 1 auto" }}>
                                    <Form.Control
                                        placeholder="Search..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>
                                {activeTab === 'holidays' && (
                                    <Button variant="primary" onClick={handleAddHoliday}>+ Add Holiday</Button>
                                )}
                                {activeTab === 'holidays' && (
                                    <Button variant="primary" onClick={handleAddMonthlyHolidays}>+ Add Monthly Holidays</Button>
                                )}
                                {activeTab === 'leaves' && (
                                    <Button variant="primary" onClick={handleAddLeave}>+ Add Leave Request</Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4"
                >
                    <Tab eventKey="holidays" title="Holidays">
                        {/* Summary Cards for Holidays */}
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-primary">{holidaysList.length}</h3>
                                        <p className="mb-0">Total Holidays</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-info">
                                            {holidaysList.filter(h => h.type === 'national').length}
                                        </h3>
                                        <p className="mb-0">National Holidays</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-success">
                                            {holidaysList.filter(h => h.type === 'religious').length}
                                        </h3>
                                        <p className="mb-0">Religious Holidays</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-warning">
                                            {holidaysList.filter(h => h.is_recurring).length}
                                        </h3>
                                        <p className="mb-0">Recurring Holidays</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Holidays Table */}
                        <div className="card">
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" />
                                        <p className="mt-2">Loading holidays...</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table hover size="sm" className="mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Holiday ID</th>
                                                    <th>Holiday Name</th>
                                                    <th>Date</th>
                                                    <th>Type</th>
                                                    <th>Recurring</th>
                                                    <th>Description</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredHolidays.length > 0 ? (
                                                    filteredHolidays.map((holiday, index) => (
                                                        <tr key={holiday.id}>
                                                            <td data-label="Holiday ID">
                                                                <div className="fw-bold">#{holiday.id}</div>
                                                            </td>
                                                            <td data-label="Holiday Name">
                                                                <div className="fw-bold">{holiday.name}</div>
                                                            </td>
                                                            <td>
                                                                <span className="text-muted">
                                                                    {formatDate(holiday.date)}
                                                                </span>
                                                            </td>
                                                            <td>{getHolidayTypeBadge(holiday.type)}</td>
                                                            <td>
                                                                {holiday.is_recurring ? (
                                                                    <Badge bg="success">Yes</Badge>
                                                                ) : (
                                                                    <Badge bg="secondary">No</Badge>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span className="text-muted" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                                                                    {holiday.description || 'No description'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-1">
                                                                    <Button 
                                                                        variant="outline-primary" 
                                                                        size="sm"
                                                                        onClick={() => handleEditHoliday(holiday)}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                    <Button 
                                                                        variant="outline-danger" 
                                                                        size="sm"
                                                                        onClick={() => handleDeleteHoliday(holiday.id)}
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4">
                                                            <Alert variant="info">
                                                                No holidays found. 
                                                                {search && ' Try adjusting your search criteria.'}
                                                                {!search && ' Click "Add Holiday" to get started.'}
                                                            </Alert>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Tab>

                    <Tab eventKey="leaves" title="Leave Requests">
                        {/* Summary Cards for Leaves */}
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-primary">{leavesList.length}</h3>
                                        <p className="mb-0">Total Requests</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-warning">
                                            {leavesList.filter(l => l.status === 'pending').length}
                                        </h3>
                                        <p className="mb-0">Pending</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-success">
                                            {leavesList.filter(l => l.status === 'approved').length}
                                        </h3>
                                        <p className="mb-0">Approved</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-danger">
                                            {leavesList.filter(l => l.status === 'rejected').length}
                                        </h3>
                                        <p className="mb-0">Rejected</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Leaves Table */}
                        <div className="card">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <Table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Employee</th>
                                                <th>Leave Type</th>
                                                <th>Start Date</th>
                                                <th>End Date</th>
                                                <th>Days</th>
                                                <th>Status</th>
                                                <th>Reason</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredLeaves.length > 0 ? (
                                                filteredLeaves.map((leave, index) => (
                                                    <tr key={leave.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <ImageShow
                                                                    url={leave.employee?.[0]?.staff?.[0]?.profile_photo_url}
                                                                    width={40}
                                                                    height={40}
                                                                    style={{
                                                                        borderRadius: '50%',
                                                                        marginRight: '10px',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div className="fw-bold">
                                                                        {leave.employee?.[0]?.staff?.[0]?.first_name} {leave.employee?.[0]?.staff?.[0]?.last_name}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {leave.employee?.[0]?.employee_id}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{getLeaveTypeBadge(leave.leave_type)}</td>
                                                        <td>
                                                            <span className="text-muted">
                                                                {formatDate(leave.start_date)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="text-muted">
                                                                {formatDate(leave.end_date)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold">
                                                                {leave.days} days
                                                            </span>
                                                        </td>
                                                        <td>{getLeaveStatusBadge(leave.status)}</td>
                                                        <td>
                                                            <span className="text-muted" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                                                                {leave.reason || 'No reason provided'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-1">
                                                                {leave.status === 'pending' && (
                                                                    <>
                                                                        <Button 
                                                                            variant="outline-success" 
                                                                            size="sm"
                                                                            onClick={() => handleApproveLeave(leave.id)}
                                                                        >
                                                                            Approve
                                                                        </Button>
                                                                        <Button 
                                                                            variant="outline-danger" 
                                                                            size="sm"
                                                                            onClick={() => handleRejectLeave(leave.id)}
                                                                        >
                                                                            Reject
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    onClick={() => handleEditLeave(leave)}
                                                                >
                                                                    Edit
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        <Alert variant="info">
                                                            No leave requests found. 
                                                            {search && ' Try adjusting your search criteria.'}
                                                            {!search && ' Click "Add Leave Request" to get started.'}
                                                        </Alert>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </Tab>

                    <Tab eventKey="balances" title="Leave Balances">
                        {/* Summary Cards for Leave Balances */}
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-primary">{leaveBalances.length}</h3>
                                        <p className="mb-0">Total Employees</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-success">
                                            {leaveBalances.reduce((sum, balance) => sum + (parseInt(balance.sick_leave) || 0), 0)}
                                        </h3>
                                        <p className="mb-0">Total Sick Leave</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-info">
                                            {leaveBalances.reduce((sum, balance) => sum + (parseInt(balance.casual_leave) || 0), 0)}
                                        </h3>
                                        <p className="mb-0">Total Casual Leave</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <h3 className="text-warning">
                                            {leaveBalances.reduce((sum, balance) => sum + (parseInt(balance.annual_leave) || 0), 0)}
                                        </h3>
                                        <p className="mb-0">Total Annual Leave</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Leave Balances Table */}
                        <div className="card">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <Table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Employee</th>
                                                <th>Sick Leave</th>
                                                <th>Casual Leave</th>
                                                <th>Annual Leave</th>
                                                <th>Maternity Leave</th>
                                                <th>Paternity Leave</th>
                                                <th>Total Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leaveBalances.length > 0 ? (
                                                leaveBalances.map((balance, index) => (
                                                    <tr key={balance.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <ImageShow
                                                                    url={balance.employee?.[0]?.staff?.[0]?.profile_photo_url}
                                                                    width={40}
                                                                    height={40}
                                                                    style={{
                                                                        borderRadius: '50%',
                                                                        marginRight: '10px',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div className="fw-bold">
                                                                        {balance.employee?.[0]?.staff?.[0]?.first_name} {balance.employee?.[0]?.staff?.[0]?.last_name}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {balance.employee?.[0]?.employee_id}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-danger">
                                                                {balance.sick_leave || 0} days
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-info">
                                                                {balance.casual_leave || 0} days
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-success">
                                                                {balance.annual_leave || 0} days
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-warning">
                                                                {balance.maternity_leave || 0} days
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-primary">
                                                                {balance.paternity_leave || 0} days
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-dark">
                                                                {(parseInt(balance.sick_leave) || 0) + 
                                                                 (parseInt(balance.casual_leave) || 0) + 
                                                                 (parseInt(balance.annual_leave) || 0) + 
                                                                 (parseInt(balance.maternity_leave) || 0) + 
                                                                 (parseInt(balance.paternity_leave) || 0)} days
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <Alert variant="info">
                                                            No leave balances found. Leave balances will be created automatically when employees are added.
                                                        </Alert>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </Tab>
                </Tabs>

                {/* Monthly Holidays Modal */}
                <Modal show={showMonthlyHolidaysModal} onHide={handleMonthlyHolidaysModalClose} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Monthly Weekend Holidays</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Select Month *</Form.Label>
                                <Form.Control
                                    type="month"
                                    value={monthlyHolidaysMonth}
                                    onChange={(e) => handleMonthlyMonthChange(e.target.value)}
                                    disabled={savingMonthlyHolidays}
                                />
                            </Form.Group>
                        </Form>

                        {monthlyHolidaysMonth ? (
                            monthlyWeekendDays.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '60px' }}>Select</th>
                                                <th>Date</th>
                                                <th>Day</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {monthlyWeekendDays.map(day => {
                                                const isSelected = selectedMonthlyWeekendDates.includes(day.date);
                                                return (
                                                    <tr key={day.date}>
                                                        <td>
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`weekend-${day.date}`}
                                                                disabled={day.existing || savingMonthlyHolidays}
                                                                checked={isSelected}
                                                                onChange={() => handleToggleMonthlyWeekendDate(day.date)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className="fw-semibold">{day.date}</span>
                                                        </td>
                                                        <td>{day.dayName}</td>
                                                        <td>
                                                            {day.existing ? (
                                                                <Badge bg="secondary">Already added</Badge>
                                                            ) : (
                                                                <Badge bg="success">Will be added</Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <Alert variant="info" className="mb-0">
                                    No weekend days found for the selected month.
                                </Alert>
                            )
                        ) : (
                            <Alert variant="secondary" className="mb-0">
                                Select a month to list all Saturdays and Sundays.
                            </Alert>
                        )}

                        {monthlyHolidaysMonth && selectedMonthlyWeekendDates.length > 0 && (
                            <Alert variant="success" className="mt-3 mb-0">
                                {selectedMonthlyWeekendDates.length} weekend day{selectedMonthlyWeekendDates.length > 1 ? 's' : ''} selected for creation.
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleMonthlyHolidaysModalClose} disabled={savingMonthlyHolidays}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSaveMonthlyHolidays}
                            disabled={savingMonthlyHolidays || selectedMonthlyWeekendDates.length === 0}
                        >
                            {savingMonthlyHolidays && (
                                <Spinner animation="border" size="sm" className="me-2" />
                            )}
                            Add Holidays
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Holiday Modal */}
                <Modal show={showHolidayModal} onHide={() => setShowHolidayModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Holiday Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={holidayForm.name}
                                    onChange={(e) => setHolidayForm({...holidayForm, name: e.target.value})}
                                    placeholder="Enter holiday name"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Date *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={holidayForm.date}
                                    onChange={(e) => setHolidayForm({...holidayForm, date: e.target.value})}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Type *</Form.Label>
                                <Form.Select
                                    value={holidayForm.type}
                                    onChange={(e) => setHolidayForm({...holidayForm, type: e.target.value})}
                                >
                                    <option value="national">National</option>
                                    <option value="religious">Religious</option>
                                    <option value="regional">Regional</option>
                                    <option value="company">Company</option>
                                    <option value="other">Other</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={holidayForm.description}
                                    onChange={(e) => setHolidayForm({...holidayForm, description: e.target.value})}
                                    placeholder="Enter holiday description"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Recurring Holiday"
                                    checked={holidayForm.is_recurring}
                                    onChange={(e) => setHolidayForm({...holidayForm, is_recurring: e.target.checked})}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowHolidayModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSaveHoliday}>
                            {editingHoliday ? 'Update' : 'Add'} Holiday
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Leave Modal */}
                <Modal show={showLeaveModal} onHide={() => setShowLeaveModal(false)} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{editingLeave ? 'Edit Leave Request' : 'Add Leave Request'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Employee *</Form.Label>
                                        <Form.Select
                                            value={leaveForm.employee_id}
                                            onChange={(e) => setLeaveForm({...leaveForm, employee_id: e.target.value})}
                                        >
                                            <option value="">Select employee...</option>
                                            {employeesList.map(employee => (
                                                <option key={employee.id} value={employee.id}>
                                                    {employee.staff?.[0]?.first_name} {employee.staff?.[0]?.last_name} ({employee.employee_id})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Leave Type *</Form.Label>
                                        <Form.Select
                                            value={leaveForm.leave_type}
                                            onChange={(e) => setLeaveForm({...leaveForm, leave_type: e.target.value})}
                                        >
                                            <option value="sick">Sick Leave</option>
                                            <option value="casual">Casual Leave</option>
                                            <option value="annual">Annual Leave</option>
                                            <option value="maternity">Maternity Leave</option>
                                            <option value="paternity">Paternity Leave</option>
                                            <option value="emergency">Emergency Leave</option>
                                            <option value="unpaid">Unpaid Leave</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Start Date *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={leaveForm.start_date}
                                            onChange={(e) => {
                                                const newForm = {...leaveForm, start_date: e.target.value};
                                                if (newForm.end_date) {
                                                    newForm.days = calculateDays(e.target.value, newForm.end_date);
                                                }
                                                setLeaveForm(newForm);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>End Date *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={leaveForm.end_date}
                                            onChange={(e) => {
                                                const newForm = {...leaveForm, end_date: e.target.value};
                                                if (newForm.start_date) {
                                                    newForm.days = calculateDays(newForm.start_date, e.target.value);
                                                }
                                                setLeaveForm(newForm);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Days</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={leaveForm.days}
                                            onChange={(e) => setLeaveForm({...leaveForm, days: e.target.value})}
                                            min="1"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            value={leaveForm.status}
                                            onChange={(e) => setLeaveForm({...leaveForm, status: e.target.value})}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="approved">Approved</option>
                                            <option value="rejected">Rejected</option>
                                            <option value="cancelled">Cancelled</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Reason</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={leaveForm.reason}
                                    onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                                    placeholder="Enter reason for leave"
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowLeaveModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSaveLeave}>
                            {editingLeave ? 'Update' : 'Add'} Leave Request
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default HolidaysLeavesManagement;
